import { snapshot, subscribe, getVersion } from './vanilla';
export * from './vanilla';
import { useRef, useState, useEffect, useReducer, useCallback, useMemo, useLayoutEffect, useDebugValue } from 'react';
import { isDeepChanged, createDeepProxy, affectedToPathList } from 'proxy-compare';

/*
export {
  unstable_createMutableSource as createMutableSource,
  unstable_useMutableSource as useMutableSource,
} from 'react'
*/
const TARGET = Symbol();
const GET_VERSION = Symbol();
const createMutableSource = (target, getVersion) => ({
  [TARGET]: target,
  [GET_VERSION]: getVersion
});
const useMutableSource = (source, getSnapshot, subscribe) => {
  const lastVersion = useRef(0);
  const currentVersion = source[GET_VERSION](source[TARGET]);
  const [state, setState] = useState(() => [
  /* [0] */
  source,
  /* [1] */
  getSnapshot,
  /* [2] */
  subscribe,
  /* [3] */
  currentVersion,
  /* [4] */
  getSnapshot(source[TARGET])]);
  let currentSnapshot = state[4];

  if (state[0] !== source || state[1] !== getSnapshot || state[2] !== subscribe || currentVersion !== state[3] && currentVersion !== lastVersion.current) {
    currentSnapshot = getSnapshot(source[TARGET]);
    setState([
    /* [0] */
    source,
    /* [1] */
    getSnapshot,
    /* [2] */
    subscribe,
    /* [3] */
    currentVersion,
    /* [4] */
    currentSnapshot]);
  }

  useEffect(() => {
    let didUnsubscribe = false;

    const checkForUpdates = () => {
      if (didUnsubscribe) {
        return;
      }

      try {
        const nextSnapshot = getSnapshot(source[TARGET]);
        const nextVersion = source[GET_VERSION](source[TARGET]);
        lastVersion.current = nextVersion;
        setState(prev => {
          if (prev[0] !== source || prev[1] !== getSnapshot || prev[2] !== subscribe) {
            return prev;
          }

          if (prev[4] === nextSnapshot) {
            return prev;
          }

          return [
          /* [0] */
          prev[0],
          /* [1] */
          prev[1],
          /* [2] */
          prev[2],
          /* [3] */
          nextVersion,
          /* [4] */
          nextSnapshot];
        });
      } catch (e) {
        // schedule update
        setState(prev => [...prev]);
      }
    };

    const unsubscribe = subscribe(source[TARGET], checkForUpdates);
    checkForUpdates();
    return () => {
      didUnsubscribe = true;
      unsubscribe();
    };
  }, [source, getSnapshot, subscribe]);
  return currentSnapshot;
};

const isSSR = typeof window === 'undefined' || /ServerSideRendering/.test(window.navigator && window.navigator.userAgent);
const useIsomorphicLayoutEffect = isSSR ? useEffect : useLayoutEffect;

const useAffectedDebugValue = (state, affected) => {
  const pathList = useRef();
  useEffect(() => {
    pathList.current = affectedToPathList(state, affected);
  });
  useDebugValue(pathList.current);
};

const mutableSourceCache = new WeakMap();

const getMutableSource = proxyObject => {
  if (!mutableSourceCache.has(proxyObject)) {
    mutableSourceCache.set(proxyObject, createMutableSource(proxyObject, getVersion));
  }

  return mutableSourceCache.get(proxyObject);
};

const useSnapshot = (proxyObject, options) => {
  const [, forceUpdate] = useReducer(c => c + 1, 0);
  const affected = new WeakMap();
  const lastAffected = useRef();
  const prevSnapshot = useRef();
  const lastSnapshot = useRef();
  useIsomorphicLayoutEffect(() => {
    lastSnapshot.current = prevSnapshot.current = snapshot(proxyObject);
  }, [proxyObject]);
  useIsomorphicLayoutEffect(() => {
    lastAffected.current = affected;

    if (prevSnapshot.current !== lastSnapshot.current && isDeepChanged(prevSnapshot.current, lastSnapshot.current, affected, new WeakMap())) {
      prevSnapshot.current = lastSnapshot.current;
      forceUpdate();
    }
  });
  const notifyInSync = options == null ? void 0 : options.sync;
  const sub = useCallback((proxyObject, cb) => subscribe(proxyObject, () => {
    const nextSnapshot = snapshot(proxyObject);
    lastSnapshot.current = nextSnapshot;

    try {
      if (lastAffected.current && !isDeepChanged(prevSnapshot.current, nextSnapshot, lastAffected.current, new WeakMap())) {
        // not changed
        return;
      }
    } catch (e) {// ignore if a promise or something is thrown
    }

    prevSnapshot.current = nextSnapshot;
    cb();
  }, notifyInSync), [notifyInSync]);
  const currSnapshot = useMutableSource(getMutableSource(proxyObject), snapshot, sub);

  if (typeof process === 'object' && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAffectedDebugValue(currSnapshot, affected);
  }

  const proxyCache = useMemo(() => new WeakMap(), []); // per-hook proxyCache

  return createDeepProxy(currSnapshot, affected, proxyCache);
};

export { useSnapshot };
